import AuthContext from 'contexts/AWSCognitoContext';
import { useCallback, useContext } from 'react';
import { FeatureFlag } from 'types/features';

/**
 * Shared helpers for the LAST_NAME_FIRST feature flag: when a tenant enables it, person
 * (contact) and employee names are shown last-name-first, first-name-last throughout the UI.
 * The reorder is UI-only and only possible where the separate first/last parts are available
 * (person party min/detail DTOs, employee min/full DTOs) — never from a pre-joined string.
 */
export interface NameParts {
    firstName?: string | null;
    lastName?: string | null;
    // True for an employee who flagged themselves on vacation (VACATION_DECLARER); drives the
    // palm-tree marker prefixed before the display name. Absent/null for non-employee entities.
    onVacation?: boolean | null;
}

export const VACATION_MARKER_EMOJI = '🌴';

/** True when an entity/dropdown option is an employee flagged on vacation (VACATION_DECLARER). */
export const isOnVacation = (entity: unknown): boolean =>
    !!entity && typeof entity === 'object' && (entity as NameParts).onVacation === true;

/**
 * Palm-tree prefix shown before an on-vacation employee's name (VACATION_DECLARER). Always prefixed
 * to the *whole* formatted name so the order stays "🌴 Last First" / "🌴 First Last" rather than
 * splitting the parts. Used for plain-string contexts (dropdown option/selected-value labels); JSX
 * surfaces use `<VacationMarker>` / `<VacationOptionLabel>` instead so the palm carries a tooltip.
 * ponytail: display is gated purely by the boolean (only settable while the flag is on) — a tenant
 * disabling the flag after the fact leaves stale markers; acceptable for this temp solution.
 */
export const vacationPrefix = (entity: unknown): string => (isOnVacation(entity) ? `${VACATION_MARKER_EMOJI} ` : '');

/** `"Last First"` when both parts are present, otherwise `null` (e.g. companies have neither). */
export const lastNameFirstName = (parts: NameParts): string | null =>
    parts.firstName && parts.lastName ? `${parts.lastName} ${parts.firstName}` : null;

/**
 * Trailing status marker the pre-joined `joined` label carries beyond the bare `"First Last"` — the
 * BE appends " (inactive)" to inactive employees' names (Employee.getFullNameWithStatus). Flipping to
 * "Last First" rebuilds the name from parts alone and would drop it, so it's re-appended after the
 * flip. Empty when `joined` doesn't start with the parts (companies, custom labels) — nothing to keep.
 */
const statusSuffix = (parts: NameParts, joined: string | null | undefined): string => {
    const base = [parts.firstName, parts.lastName].filter(Boolean).join(' ');
    if (!joined || !base || !joined.startsWith(base)) {
        return '';
    }
    // Only an empty or whitespace-separated remainder is a real marker (" (inactive)"); a run-on like
    // base "Ann Lee" against "Ann Leeves" is a partial-word match, not a suffix.
    const suffix = joined.slice(base.length);
    return suffix === '' || /^\s/.test(suffix) ? suffix : '';
};

/** Reads first/last off an arbitrary dropdown option and returns the flipped name, or `null`. */
export const optionLastNameFirst = (option: unknown): string | null =>
    option && typeof option === 'object' ? lastNameFirstName(option as NameParts) : null;

/** Employee display name, flipped to `"Last First"` when the flag is on and both parts exist. */
export const formatEmployeeName = (parts: NameParts, lastNameFirst: boolean): string =>
    vacationPrefix(parts) +
    ((lastNameFirst && lastNameFirstName(parts)) || [parts.firstName, parts.lastName].filter(Boolean).join(' '));

/** An entity that carries a pre-joined display name plus (for persons/employees) the separate parts. */
export interface NamedEntityParts extends NameParts {
    name?: string | null;
}

/**
 * Display name for an entity that already has a pre-joined `name`: flip to `"Last First"` when the
 * flag is on and both parts are present, otherwise fall back to the pre-joined `name`. This is the
 * single primitive display sites use so a missing/absent first+last (companies, non-person entities,
 * DTOs that don't carry parts) transparently keeps the existing behaviour.
 */
export const displayName = (entity: NamedEntityParts, lastNameFirst: boolean): string => {
    const flipped = lastNameFirst ? lastNameFirstName(entity) : null;
    return (
        vacationPrefix(entity) +
        (flipped
            ? flipped + statusSuffix(entity, entity.name)
            : entity.name || [entity.firstName, entity.lastName].filter(Boolean).join(' '))
    );
};

/**
 * Dropdown-option display label used by the RHF / list-filter base selects: the `optionLastNameFirst`
 * flip when the flag is on (else the resolved `fallbackLabel`), with the vacation marker prefixed.
 * Single primitive so every select shows the palm tree without duplicating the flip+fallback logic.
 */
export const optionDisplayLabel = (
    option: unknown,
    lastNameFirst: boolean,
    // The label field is allowed to be null, and concatenating that into the display string
    // puts the word "null" in the input.
    fallbackLabel: string | null | undefined
): string => {
    const label = fallbackLabel ?? '';
    const flipped = lastNameFirst ? optionLastNameFirst(option) : null;
    return vacationPrefix(option) + (flipped ? flipped + statusSuffix(option as NameParts, label) : label);
};

/**
 * Hook returning a stable `displayName` formatter bound to the current LAST_NAME_FIRST state. Use it
 * in components; for hook-free contexts (column `cell` callbacks, non-component helpers) call
 * `displayName(entity, lastNameFirst)` directly with a flag value threaded in from the component.
 */
export const useEmployeeNameFormatter = (): ((entity: NamedEntityParts) => string) => {
    const lastNameFirst = useLastNameFirst();
    return useCallback((entity: NamedEntityParts) => displayName(entity, lastNameFirst), [lastNameFirst]);
};

/**
 * Whether LAST_NAME_FIRST is enabled for the current user. Reads the auth context directly
 * (instead of `useAuth`, which throws without a provider) so the base select inputs that call
 * this can still render in isolation — e.g. component tests without an AuthProvider.
 */
export const useLastNameFirst = (): boolean =>
    !!useContext(AuthContext)?.user?.enabledFeatures?.includes(FeatureFlag.LAST_NAME_FIRST);

// ponytail: `fullName`/`name` are string literals rather than a shared constant because they mirror
// the exact BE field names (Employee.fullName, Party.name) already hardcoded at ~200 dropdown call sites.

/** Matches `fullName` or a nested path ending in it, e.g. `manager.fullName` (Employee search field). */
const isFullNameField = (field: string): boolean => /(^|\.)fullName$/.test(field);

/** Matches `name` or a nested path ending in it, e.g. `clientParty.name` (Party search field). */
const isNameField = (field: string): boolean => /(^|\.)name$/.test(field);

/**
 * Of a dropdown's search fields, returns the ones that identify a person/company display name —
 * `fullName` (Employee), or `name` paired with a sibling `partyNumber` on the same entity (Party;
 * `partyNumber` rules out unrelated `name` fields like Team/Office/Pricelist). These are the fields
 * where a "Last First" typed query should also be tried under LAST_NAME_FIRST.
 */
export const reversibleNameSearchFields = (fields: string[]): Set<string> => {
    const eligible = new Set<string>();
    fields.forEach((field) => {
        if (isFullNameField(field)) {
            eligible.add(field);
        } else if (isNameField(field) && fields.includes(`${field.slice(0, -'name'.length)}partyNumber`)) {
            eligible.add(field);
        }
    });
    return eligible;
};

/**
 * Rotates a "Last First[ Middle]" search term to "First[ Middle] Last" (moves the first word to the
 * end) so it can additionally be matched against a `firstName + " " + lastName` stored value. Returns
 * `null` for a single word, where there's nothing to reorder.
 */
export const rotateNameSearchTerm = (value: string): string | null => {
    const words = value.trim().split(/\s+/).filter(Boolean);
    return words.length > 1 ? [...words.slice(1), words[0]].join(' ') : null;
};

/** Two letters for an avatar: the first and last words' initials, or a single word's first two letters. */
export const getInitials = (name: string): string => {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
};

/** A person's initials from the name parts, whichever order the name is shown in; `getInitials` of `name` without them. */
export const nameInitials = ({ firstName, lastName, name }: NamedEntityParts): string =>
    firstName && lastName ? `${firstName[0]}${lastName[0]}`.toUpperCase() : getInitials(name ?? '');
